import { Fragment } from 'react';
import {
    Link,
    useLocation,
} from 'react-router-dom';

import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

// Human labels for known route segments. Anything missing falls back to
// title-case, and id-like segments collapse to "Details".
const SEGMENT_LABELS: Record<string, string> = {
    dashboard: 'Dashboard',
    'related-parties': 'Related Parties',
    'my-parties': 'My Parties',
    add: 'Add New',
    requests: 'Requests',
    favorites: 'Favorites',
    blocked: 'Blocked',
    map: 'Find on Map',
    party: 'Party',
    transactions: 'Transactions',
    reviews: 'Reviews',
    inventory: 'Inventory',
    products: 'Products',
    groups: 'Groups',
    categories: 'Categories',
    units: 'Units',
    create: 'Create',
    catalog: 'Catalog Import',
    detail: 'Details',
    edit: 'Edit',
    orders: 'Orders',
    incoming: 'Incoming',
    outgoing: 'Outgoing',
    'respond-orders': 'Respond to Orders',
    quotations: 'Quotations',
    request: 'Request',
    respond: 'Respond',
    announcements: 'Schemes & Offers',
    reports: 'Reports',
    settings: 'Settings',
    cart: 'Cart',
    contact: 'Contact',
};

interface Crumb {
    label: string;
    href: string;
}

const looksLikeId = (segment: string): boolean => (
    /\d/.test(segment) || segment.length > 24
);

const titleCase = (segment: string): string => segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const labelFor = (segment: string): string => {
    if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment];
    if (looksLikeId(segment)) return 'Details';
    return titleCase(segment);
};

function buildCrumbs(pathname: string): Crumb[] {
    const segments = pathname.split('/').filter(Boolean);
    let href = '';
    return segments.map((segment) => {
        href += `/${segment}`;
        return { label: labelFor(segment), href };
    });
}

export function Breadcrumbs() {
    const location = useLocation();
    const crumbs = buildCrumbs(location.pathname);

    if (crumbs.length === 0) return null;

    return (
        <Breadcrumb>
            <BreadcrumbList>
                {crumbs.map((crumb, index) => {
                    const isLast = index === crumbs.length - 1;
                    return (
                        <Fragment key={crumb.href}>
                            <BreadcrumbItem>
                                {isLast ? (
                                    <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                                ) : (
                                    <BreadcrumbLink
                                        asChild
                                        className="hidden md:inline-flex"
                                    >
                                        <Link to={crumb.href}>{crumb.label}</Link>
                                    </BreadcrumbLink>
                                )}
                            </BreadcrumbItem>
                            {!isLast && (
                                <BreadcrumbSeparator className="hidden md:flex" />
                            )}
                        </Fragment>
                    );
                })}
            </BreadcrumbList>
        </Breadcrumb>
    );
}
